import { useEffect, useRef, useState } from 'react'
import { Menu, MenuDivider, MenuItem } from '../components/ui'
import type { MyBusinessLines } from '../api/businessLines'
import { GENERAL, setBranch } from '../lib/branch'

/**
 * ADR-019 (Equal Point): chip del ramo nella barra in alto, col nome e il colore del ramo in cui si lavora.
 * Il menu elenca i rami dell'utente e, per chi la vede, la vista Generale. Alt+1…5 apre il ramo n, Alt+0 la Generale.
 */
export function BranchChip({ mine, branch }: { mine: MyBusinessLines; branch: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const current = mine.lines.find(l => l.slug === branch)
  const label = current?.name.replace(/^Equal\s+/, '') ?? 'Generale'

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Solo la fila dei numeri: Alt+tastierino scrive i caratteri (Alt+0200 = È) e non deve cambiare ramo.
      if (!e.altKey || e.ctrlKey || e.metaKey || e.repeat || !/^Digit[0-9]$/.test(e.code)) return
      const el = document.activeElement as HTMLElement | null
      if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return
      const n = Number(e.code.slice(5))
      const target = n === 0 ? (mine.general ? GENERAL : null) : mine.lines[n - 1]?.slug ?? null
      if (!target) return
      e.preventDefault()
      setBranch(target)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mine])

  // Con un solo ramo e senza Generale non c'è niente da scegliere: il chip dice solo dove si è.
  const choices = mine.lines.length + (mine.general ? 1 : 0)

  const dot = <span key={branch} className="md-branch-chip__dot" style={{ background: current?.color ?? '#B8A48C', color: current?.color ?? '#B8A48C' }} aria-hidden="true" />
  // Una sola scelta: il chip dice solo dove si è, come testo leggibile (un pulsante disabilitato uscirebbe dal Tab).
  if (choices <= 1) {
    return <span className="md-branch-chip md-branch-chip--static" role="status" aria-label={`Ramo: ${label}`}>{dot}<span className="md-branch-chip__label">{label}</span></span>
  }

  return (
    <>
      <button
        ref={ref}
        type="button"
        className="md-branch-chip md-state"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Ramo: ${label}`}
        aria-keyshortcuts="Alt+0 Alt+1 Alt+2 Alt+3 Alt+4 Alt+5"
        title="Cambia ramo (Alt+0 Generale, Alt+1…5 i rami)"
        onClick={() => setOpen(o => !o)}
      >
        {dot}
        <span className="md-branch-chip__label">{label}</span>
        <span className="material-symbols-outlined md-icon md-icon--20" aria-hidden="true">expand_more</span>
      </button>
      <Menu anchorRef={ref} open={open} onClose={() => setOpen(false)} aria-label="Rami">
        {mine.general && (
          <MenuItem icon="hub" selected={branch === GENERAL} onSelect={() => { setBranch(GENERAL); setOpen(false) }}>
            Generale
          </MenuItem>
        )}
        {mine.general && mine.lines.length > 0 && <MenuDivider />}
        {mine.lines.map(l => (
          <MenuItem key={l.slug} icon="circle" selected={branch === l.slug} onSelect={() => { setBranch(l.slug); setOpen(false) }}>
            {l.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
